<template>
  <div>
    <v-stepper-step :complete="currentStep > 3" step="3">
      <h3>WHAT DOES YOUR ITEM CONTAIN?</h3>
      <div class="text-caption">
        Please provide short description of the item/s content, you'll
        need to add a suitable description for each type of content you're sending.
        <p v-show="currentStep == 3">
          <span class="red--text"><strong>* </strong></span>= Mandatory field
        </p>
      </div>
    </v-stepper-step>
    <v-stepper-content step="3">
      <v-form v-model="contentFormValid">
        <fieldset class="sr-only-fieldset">
          <legend class="sr-only">What does your item contain?</legend>
          <v-container v-for="(item, index) in contentItems" :key="item.key">
            <v-row>
              <v-col cols="12" md="10">
                <p class="font-weight-black">Item {{ index + 1 }}</p>
              </v-col>
              <v-col cols="12" md="2">
                <v-btn
                  block
                  color="primary"
                  v-if="index > 0"
                  @click="removeItem(index)"
                >
                  Delete
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="12" order="1">
                <v-text-field
                  class="hasValidationErrorIcon"
                  prepend-inner-icon="mdi-information-outline"
                  v-model="item.description"
                  :rules="descriptionRules"
                  :counter="35"
                  label="Description"
                  placeholder="Description"
                  required
                  aria-required="true"
                  :aria-describedby="'content-item-description'"
                >
                  <template #label>
                    <h4 class="black--text">
                      <span class="red--text"><strong>* </strong></span>DESCRIPTION
                      <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                    </h4>
                  </template>

                  <!-- Hidden span for screen readers -->
                  <template v-slot:append-outer>
                    <span id="content-item-description" class="sr-only">
                      {{ item.description ? item.description.length : 0 }} of 35 characters used
                    </span>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6" order="2">
                <v-text-field
                  class="hasValidationErrorIcon itemQuantity"
                  prepend-inner-icon="mdi-information-outline"
                  v-model.number="item.quantity"
                  :rules="quantityRules"
                  label="Quantity"
                  placeholder="Quantity"
                  type="number"
                  required
                  aria-required="true"
                >
                  <template #label>
                    <h4 class="black--text">
                      <span class="red--text"><strong>* </strong></span>TOTAL QUANTITY
                      <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                    </h4>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6" order="3">
                <v-text-field
                  class="hasValidationErrorIcon"
                  prepend-inner-icon="mdi-information-outline"
                  v-model.number="item.weight"
                  :rules="weightRules"
                  label="Weight"
                  placeholder="Weight"
                  type="number"
                  required
                  aria-required="true"
                >
                  <template #label>
                    <h4 class="black--text">
                      <span class="red--text"><strong>* </strong></span>TOTAL NET WEIGHT (grams)
                      <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                    </h4>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4" order="5" order-md="4">
                <v-select
                  :items="countries"
                  label="Country of Origin"
                  item-text="name"
                  item-value="id"
                  placeholder="Country of Origin"
                  v-model="item.countryOfOriginId"
                  :rules="countryOfOriginRules"
                  required
                  aria-required="true"
                >
                  <template #label>
                    <h4 class="black--text">
                      <span class="red--text"><strong>* </strong></span>COUNTRY OF ORIGIN
                      <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                    </h4>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="4" order="4" order-md="5">
                <v-text-field
                  class="hasValidationErrorIcon itemValue"
                  prepend-inner-icon="mdi-information-outline"
                  v-model.number="item.value"
                  :rules="(requiresItmattForDocuments && sendingDocument) ? valueRulesAllowingZero : valueRules"
                  label="Total Value"
                  placeholder="Total Value"
                  type="number"
                  required
                  aria-required="true"
                >
                  <template #label>
                    <h4 class="black--text">
                      <span class="red--text"><strong>* </strong></span>TOTAL VALUE (€)
                      <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                    </h4>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="4" order="6" v-if="!sendingDocument">
                <v-text-field
                  id="content-items-hscode"
                  v-model="item.hsCode"
                  :counter="10"
                  :rules="hscodeRules"
                  label="HS Code"
                  placeholder="HS Code"
                  required
                  aria-required="true"
                  :aria-describedby="'content-item-hscode'"
                >
                  <template #label>
                    <!-- for visibilty see https://stackoverflow.com/a/51683904 -->
                    <h4 class="black--text d-none d-lg-block">
                      <span class="red--text"><strong>* </strong></span>
                      HS CODE <span>(Use the
                        <a href="https://www.tariffnumber.com/" target="_blank" rel="noopener">
                          HS code lookup tool
                        </a>
                      )
                    </span>
                    <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                    </h4>
                    <h4 class="black--text d-block">
                      <span class="red--text"><strong>* </strong></span>
                      HS CODE
                    </h4>
                  </template>

                  <!-- Hidden span for screen readers -->
                  <template v-slot:append-outer>
                    <span id="content-item-hscode" class="sr-only">
                      {{ item.description ? item.description.length : 0 }} of 10 characters used
                    </span>
                  </template>
                </v-text-field>
                
                <p class="h6 d-block d-lg-none">
                  Use the
                  <a href="https://www.tariffnumber.com/" target="_blank" rel="noopener">HS code lookup tool</a>
                </p>

              </v-col>

              <v-col cols="12" md="4" order="6" v-if="sendingDocument && requiresItmattForDocuments">
                <v-select
                  :items="hsCodes"
                  label="HS CODES"
                  item-text="description"
                  item-value="id"
                  placeholder="HS CODES"                
                  :rules="hscodeRulesDropDow"
                  required
                  aria-required="true"
                  @change="onHsCodesChange($event, item)"
                >                
                  <template #label>
                    <h4 class="black--text">
                      <span class="red--text"><strong>* </strong></span>HS CODE
                      <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                    </h4>
                  </template>
                </v-select>
              </v-col>

            </v-row>
            <v-row>
              <v-divider></v-divider>
            </v-row>
          </v-container>
          <v-container style="margin-bottom: 20px; overflow: hidden">
            <v-row>
              <v-col cols="12" md="2">
                <v-btn @click="addItem()" color="primary" block>
                  Add Another Item
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </fieldset>
      </v-form>
      <div v-if="totalParcelWeightExceedsMaximumLimit()" style="padding-bottom: 38px" class="error--text">
        The total parcel weight cannot exceed {{ maximumParcelWeightInGrams / 1000 }} kilograms.
      </div>
      <v-btn 
        color="primary" 
        @click="goNext" 
        :aria-disabled="nextButtonDisabled"
        :disabled="nextButtonDisabled">
        Next
      </v-btn>

      <div
        class="sr-only"
        aria-live="polite"
        v-if="nextButtonDisabled"
      >
        Please complete all required content item information fields to proceed.
      </div>

      <v-btn 
        text @click="currentStep = 2"
        aria-label="Go back to step 2 to specify content item category information"> Back </v-btn>
    </v-stepper-content>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import appConstants from "../../constants/AppConstants";
import validationRules from "../../helpers/ValidationRules";

const { mapGetters: mapDeclarationGetters } = createNamespacedHelpers(
  "declarations"
);

export default {
  name: "Step03ContentItems",
  data() {
    return {
      contentItems: [
        {
          key: 1,
          description: (this.requiresItmattForDocuments && this.sendingDocument) ? "Documents" : "",
          hsCode: "",
          quantity: null,
          value: null,
          weight: null,
          countryOfOriginId: (this.requiresItmattForDocuments && this.sendingDocument) ? this.getCountryIdByIsoCode('MT') : 0,
        },
      ],
      contentFormValid: false,
      maximumParcelWeightLimitExceeded: false,
      maximumParcelWeightInGrams: appConstants.MaximumParcelWeightInGrams,
      ...validationRules
    };
  },
  computed: {
    ...mapDeclarationGetters(["countries", "hsCodes", "requiresItmattForDocuments", "sendingDocument"]),
    currentStep: {
      set(currentStep) {
        this.$store.dispatch("declarations/setCurrentStep", currentStep);
      },
      get() {
        return this.$store.getters["declarations/currentStep"];
      },
    },
    nextButtonDisabled() {
      return (!this.contentFormValid || this.maximumParcelWeightLimitExceeded);
    }
  },
  methods: {
    addItem() {
      var largestKey = Math.max.apply(
        Math,
        this.contentItems.map(function (o) {
          return o.key;
        })
      );

      const newItem = {
        key: largestKey + 1,
        description: (this.requiresItmattForDocuments && this.sendingDocument) ? "Documents" : "",
        hsCode: "",
        quantity: null,
        value: null,
        weight: null,
        countryOfOriginId: (this.requiresItmattForDocuments && this.sendingDocument) ? this.getCountryIdByIsoCode('MT') : 0,
      };
      this.contentItems.push(newItem);
    },
    removeItem(index) {
      this.contentItems.splice(index, 1);
    },
    goNext() {
      this.$store.dispatch(
        "declarations/Step03ContentItems/setContentItems",
        this.contentItems
      );
      this.$store.dispatch("declarations/setCurrentStep", 4);
    },
    getCountryIdByIsoCode: function (isoCode) {
      if (isoCode) {
        
        const country = this.countries.find(function (country) {

          return country?.code?.toLowerCase() == isoCode.toLowerCase();
        });

        return country ? country.id : 0;
      }

      return 0;
    },
    onHsCodesChange(event, item) {
        let hsCodeObject = this.hsCodes.find(hsCodeElement => hsCodeElement.id == event);

        if(hsCodeObject)
          item.hsCode = hsCodeObject.hsCode;        
    },
    onShowTheStep() {
      
      if(this.requiresItmattForDocuments && this.sendingDocument)
      {
        if(this.contentItems.length > 0)
        {          
          this.contentItems[0].description = "Documents";
          this.contentItems[0].countryOfOriginId = this.getCountryIdByIsoCode('MT');
        }
        
      }
      else
      {
        if(this.contentItems.length > 0)
        {
          this.contentItems[0].description = "";
          this.contentItems[0].countryOfOriginId = 0;
        }
      }
    },
    totalParcelWeightExceedsMaximumLimit() {
      const totalParcelWeightInGrams = 
        this.contentItems.reduce((accumulator, { quantity, weight }) => {
          return ((quantity && weight) && (quantity > 0 && weight > 0)) ?
            accumulator + (quantity * weight) : accumulator;
        }, 0);

      this.maximumParcelWeightLimitExceeded = 
        (totalParcelWeightInGrams > this.maximumParcelWeightInGrams);

      return this.maximumParcelWeightLimitExceeded;
    }
  },
  mounted() {
  }
};
</script>
<style>
label[for="content-items-hscode"] {
  pointer-events: auto !important;
}
</style>